import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { marketStatuses, type MarketListing } from "@shared/schema";
import { Package, Eye, Heart, Clock, Edit, Trash2, ArrowLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { PageContainer } from "@/components/layout/PageContainer";

export default function MyMarket() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: listings, isLoading } = useQuery<MarketListing[]>({
    queryKey: ["/api/market", user?.uid],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (user?.uid) params.append("userId", user.uid);

      const res = await fetch(`/api/market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch my listings");
      return res.json();
    },
    enabled: !!user,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/market/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/market", user?.uid] });
      toast({ title: "상태가 변경되었습니다" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/market/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/market", user?.uid] });
      toast({ title: "게시글이 삭제되었습니다" });
    },
  });

  const filteredListings = listings?.filter(listing =>
    statusFilter === "all" || listing.status === statusFilter
  );

  const formatPrice = (cents: number) => {
    if (cents === 0) return "무료나눔";
    if (cents === -1) return "가격협의";
    return `$${(cents / 100).toFixed(0)}`;
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">로그인이 필요합니다</h2>
        <Button onClick={() => setLocation("/")}>홈으로 돌아가기</Button>
      </div>
    );
  }

  return (
    <PageContainer size="default">
          <Button variant="ghost" onClick={() => setLocation("/market")} className="mb-4" data-testid="button-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            목록으로
          </Button>

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold" data-testid="text-title">내 판매글</h1>
            <Link href="/market/register">
              <Button data-testid="button-register">
                새 판매글 등록
              </Button>
            </Link>
          </div>

          {/* Filter */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">상태 필터:</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">전체</SelectItem>
                    {marketStatuses.map(status => (
                      <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {isLoading ? (
            <Card>
              <CardContent className="py-16 text-center">
                <p>로딩 중...</p>
              </CardContent>
            </Card>
          ) : filteredListings && filteredListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredListings.map((listing) => (
                <Card key={listing.id} data-testid={`card-listing-${listing.id}`}>
                  <div className="relative h-48 bg-muted">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-muted-foreground/30" />
                      </div>
                    )}
                    <Badge
                      className={`absolute top-2 right-2 ${
                        listing.status === "available" ? "bg-green-600" :
                        listing.status === "reserved" ? "bg-yellow-600" :
                        "bg-gray-600"
                      }`}
                    >
                      {marketStatuses.find(s => s.id === listing.status)?.label}
                    </Badge>
                  </div>

                  <CardHeader className="pb-3">
                    <h3 className="font-semibold line-clamp-2">{listing.title}</h3>
                    <div className="text-xl font-bold text-primary">
                      {formatPrice(listing.priceCents || 0)}
                    </div>
                  </CardHeader>

                  <CardContent className="pb-3">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {listing.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {listing.favorites || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(listing.createdAt), { addSuffix: true, locale: ko })}
                      </span>
                    </div>
                  </CardContent>

                  <CardFooter className="flex flex-col gap-2 pt-3 border-t">
                    <div className="flex gap-2 w-full">
                      <Link href={`/market/${listing.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                          보기
                        </Button>
                      </Link>
                      <Link href={`/market/edit/${listing.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full" data-testid={`button-edit-${listing.id}`}>
                          <Edit className="w-3 h-3 mr-1" />
                          수정
                        </Button>
                      </Link>
                    </div>
                    <div className="flex gap-2 w-full">
                      <Select
                        value={listing.status}
                        onValueChange={(value) => updateStatusMutation.mutate({ id: listing.id, status: value })}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {marketStatuses.map(status => (
                            <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm("정말 삭제하시겠습니까?")) {
                            deleteMutation.mutate(listing.id);
                          }
                        }}
                        data-testid={`button-delete-${listing.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-lg font-medium mb-2">등록한 게시글이 없습니다</p>
                <p className="text-sm text-muted-foreground mb-6">
                  첫 번째 판매글을 등록해보세요!
                </p>
                <Link href="/market/register">
                  <Button>
                    판매글 등록하기
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
    </PageContainer>
  );
}
